import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Save, X, AlertCircle, ArrowLeft, Phone } from 'lucide-react';
import { RTIOfficerFormData, POST_TYPES, ROMAN_NUMERALS } from '../../types/rti-officers.types';
import { toast } from 'react-toastify';
import { rtiOfficersService } from '../../services/api/rtiOfficersService';

const RTIOfficersForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState<RTIOfficerFormData>({
    section_number: 1,
    serial_number: 1,
    name: '',
    designation: '',
    post: 'PIO',
    subjects_allotted: '',
    address: '',
    mobile_number: '',
    email: '',
    is_active: true,
    display_order: 0,
  });

  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof RTIOfficerFormData, string>>>({});

  useEffect(() => {
    if (isEditMode && id) {
      fetchOfficer(parseInt(id));
    }
  }, [id, isEditMode]);

  const fetchOfficer = async (officerId: number) => {
    try {
      setFetchingData(true);
      const response = await rtiOfficersService.getById(officerId);
      if (response.success) {
        const data = response.data;
        setFormData({
          section_number: data.section_number,
          serial_number: data.serial_number,
          name: data.name || '',
          designation: data.designation || '',
          post: data.post || 'PIO',
          subjects_allotted: data.subjects_allotted || '',
          address: data.address || '',
          mobile_number: data.mobile_number || '',
          email: data.email || '',
          is_active: data.is_active,
          display_order: data.display_order || 0,
        });
      }
    } catch (error) {
      toast.error('Failed to fetch RTI officer details');
      console.error(error);
      navigate('/rti-officers');
    } finally {
      setFetchingData(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : type === 'number' ? parseInt(value) || 0 : value,
    });

    if (errors[name as keyof RTIOfficerFormData]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof RTIOfficerFormData, string>> = {};

    if (!formData.section_number || formData.section_number < 1 || formData.section_number > 6) {
      newErrors.section_number = 'Please select a valid section (1-6)';
    }
    if (!formData.serial_number || formData.serial_number < 1) {
      newErrors.serial_number = 'Serial number must be greater than 0';
    }
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!formData.designation.trim()) {
      newErrors.designation = 'Designation is required';
    }
    if (!formData.post) {
      newErrors.post = 'Post is required';
    }
    if (!formData.subjects_allotted.trim()) {
      newErrors.subjects_allotted = 'Subjects alloted is required';
    }
    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }
    if (!formData.mobile_number.trim()) {
      newErrors.mobile_number = 'Mobile number is required';
    }
    if (formData.display_order < 0) {
      newErrors.display_order = 'Display order must be 0 or greater';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    try {
      setLoading(true);

      if (isEditMode && id) {
        await rtiOfficersService.update(parseInt(id), formData);
        toast.success('RTI Officer updated successfully');
      } else {
        await rtiOfficersService.create(formData);
        toast.success('RTI Officer created successfully');
      }

      navigate('/rti-officers');
    } catch (error: any) {
      const message = error.response?.data?.message ||
        (isEditMode ? 'Failed to update RTI officer' : 'Failed to create RTI officer');
      toast.error(message);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (fetchingData) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-gray-600">Loading RTI officer data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditMode ? 'Edit RTI Officer' : 'Create RTI Officer'}
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            {isEditMode
              ? 'Update RTI officer information'
              : 'Add a new RTI officer to the directory'}
          </p>
        </div>
        <Link
          to="/rti-officers"
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
        >
          <ArrowLeft size={18} className="mr-2" />
          Back to List
        </Link>
      </div>

      {/* Form Card */}
      <div className="bg-white shadow-sm border border-gray-200 rounded-lg">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Section and Serial Number Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Section Number */}
            <div>
              <label htmlFor="section_number" className="block text-sm font-medium text-gray-700 mb-2">
                Section <span className="text-red-500">*</span>
              </label>
              <select
                id="section_number"
                name="section_number"
                value={formData.section_number}
                onChange={handleChange}
                className={`block w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
                  errors.section_number
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:ring-blue-500'
                }`}
              >
                {[1, 2, 3, 4, 5, 6].map(num => (
                  <option key={num} value={num}>
                    Section {ROMAN_NUMERALS[num - 1]} - {num}
                  </option>
                ))}
              </select>
              {errors.section_number && (
                <div className="flex items-center mt-2 text-sm text-red-600">
                  <AlertCircle size={14} className="mr-1 flex-shrink-0" />
                  <span>{errors.section_number}</span>
                </div>
              )}
            </div>

            {/* Serial Number */}
            <div>
              <label htmlFor="serial_number" className="block text-sm font-medium text-gray-700 mb-2">
                Serial Number <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                id="serial_number"
                name="serial_number"
                value={formData.serial_number}
                onChange={handleChange}
                min="1"
                className={`block w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
                  errors.serial_number
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:ring-blue-500'
                }`}
              />
              {errors.serial_number && (
                <div className="flex items-center mt-2 text-sm text-red-600">
                  <AlertCircle size={14} className="mr-1 flex-shrink-0" />
                  <span>{errors.serial_number}</span>
                </div>
              )}
            </div>
          </div>

          {/* Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`block w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
                errors.name
                  ? 'border-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-blue-500'
              }`}
              placeholder="e.g., Sri. V. Kanakaratnam"
            />
            {errors.name && (
              <div className="flex items-center mt-2 text-sm text-red-600">
                <AlertCircle size={14} className="mr-1 flex-shrink-0" />
                <span>{errors.name}</span>
              </div>
            )}
          </div>

          {/* Designation */}
          <div>
            <label htmlFor="designation" className="block text-sm font-medium text-gray-700 mb-2">
              Designation <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="designation"
              name="designation"
              value={formData.designation}
              onChange={handleChange}
              className={`block w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
                errors.designation
                  ? 'border-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-blue-500'
              }`}
              placeholder="e.g., Engineer-in-Chief"
            />
            {errors.designation && (
              <div className="flex items-center mt-2 text-sm text-red-600">
                <AlertCircle size={14} className="mr-1 flex-shrink-0" />
                <span>{errors.designation}</span>
              </div>
            )}
          </div>

          {/* Post */}
          <div>
            <label htmlFor="post" className="block text-sm font-medium text-gray-700 mb-2">
              Post <span className="text-red-500">*</span>
            </label>
            <select
              id="post"
              name="post"
              value={formData.post}
              onChange={handleChange}
              className={`block w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
                errors.post
                  ? 'border-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-blue-500'
              }`}
            >
              {POST_TYPES.map(post => (
                <option key={post} value={post}>{post}</option>
              ))}
            </select>
            {errors.post && (
              <div className="flex items-center mt-2 text-sm text-red-600">
                <AlertCircle size={14} className="mr-1 flex-shrink-0" />
                <span>{errors.post}</span>
              </div>
            )}
          </div>

          {/* Subjects Alloted */}
          <div>
            <label htmlFor="subjects_allotted" className="block text-sm font-medium text-gray-700 mb-2">
              Subjects Alloted <span className="text-red-500">*</span>
            </label>
            <textarea
              id="subjects_allotted"
              name="subjects_allotted"
              value={formData.subjects_allotted}
              onChange={handleChange}
              rows={3}
              className={`block w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-colors resize-none ${
                errors.subjects_allotted
                  ? 'border-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-blue-500'
              }`}
              placeholder="e.g., Administration, Budget, Accounts and loans"
            />
            {errors.subjects_allotted && (
              <div className="flex items-center mt-2 text-sm text-red-600">
                <AlertCircle size={14} className="mr-1 flex-shrink-0" />
                <span>{errors.subjects_allotted}</span>
              </div>
            )}
          </div>

          {/* Address */}
          <div>
            <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
              Address <span className="text-red-500">*</span>
            </label>
            <textarea
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              rows={2}
              className={`block w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-colors resize-none ${
                errors.address
                  ? 'border-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-blue-500'
              }`}
              placeholder="e.g., 2nd FLOOR, SRTGN BHAVAN, ERRUM MANZIL COLONY, HYDERABAD"
            />
            {errors.address && (
              <div className="flex items-center mt-2 text-sm text-red-600">
                <AlertCircle size={14} className="mr-1 flex-shrink-0" />
                <span>{errors.address}</span>
              </div>
            )}
          </div>

          {/* Mobile Number and Email Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Mobile Number */}
            <div>
              <label htmlFor="mobile_number" className="block text-sm font-medium text-gray-700 mb-2">
                Mobile Number <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Phone size={18} className="text-gray-400" />
                </div>
                <input
                  type="text"
                  id="mobile_number"
                  name="mobile_number"
                  value={formData.mobile_number}
                  onChange={handleChange}
                  className={`block w-full pl-10 pr-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
                    errors.mobile_number
                      ? 'border-red-500 focus:ring-red-500'
                      : 'border-gray-300 focus:ring-blue-500'
                  }`}
                  placeholder="91211 35670"
                />
              </div>
              {errors.mobile_number && (
                <div className="flex items-center mt-2 text-sm text-red-600">
                  <AlertCircle size={14} className="mr-1 flex-shrink-0" />
                  <span>{errors.mobile_number}</span>
                </div>
              )}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email (Optional)
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                placeholder="officer@example.com"
              />
            </div>
          </div>

          {/* Display Order and Active Status Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Display Order */}
            <div>
              <label htmlFor="display_order" className="block text-sm font-medium text-gray-700 mb-2">
                Display Order
              </label>
              <input
                type="number"
                id="display_order"
                name="display_order"
                value={formData.display_order}
                onChange={handleChange}
                min="0"
                className={`block w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
                  errors.display_order
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:ring-blue-500'
                }`}
              />
              {errors.display_order ? (
                <div className="flex items-center mt-2 text-sm text-red-600">
                  <AlertCircle size={14} className="mr-1 flex-shrink-0" />
                  <span>{errors.display_order}</span>
                </div>
              ) : (
                <p className="mt-2 text-sm text-gray-500">
                  Lower numbers appear first in the list
                </p>
              )}
            </div>

            {/* Active Status */}
            <div className="flex items-end pb-2">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                />
                <span className="ml-2 text-sm font-medium text-gray-700">
                  Active
                </span>
              </label>
              <span className="ml-2 text-sm text-gray-500">
                (Visible to public)
              </span>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end gap-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/rti-officers')}
              disabled={loading}
              className="inline-flex items-center px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <X size={18} className="mr-2" />
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center px-4 py-2.5 border border-transparent rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  {isEditMode ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>
                  <Save size={18} className="mr-2" />
                  {isEditMode ? 'Update Officer' : 'Create Officer'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RTIOfficersForm;
